import React, { useEffect, useState } from "react";
import { Button, Card, Form, Modal, Row, Col, Spinner } from "react-bootstrap";
import { FiDollarSign, FiPlus } from "react-icons/fi";
import { showSuccess } from "@infrastructure/api/utils/toastNotifications";
import { ErrorResponse } from "@core/types/errorResponse";
import { useApiError } from "@infrastructure/api/hooks/useApiError";
import { useErrorModalStore } from "@infrastructure/api/store/errorModalStore";
import { useScopeId } from "@core/hooks/useRouteParamId";
import { useFinanceCategoryAction } from "@features/admin/finance/category/hooks/useFinanceCategoryAction";
import { useFinanceCategoryOptions } from "@features/admin/finance/category/hooks/useFinanceCategoryOptions";
import { CreateFinanceCategoryRequest } from "@features/admin/finance/category/types/financeCategoryRequests";
import InfoTooltip from "@core/components/InfoTooltip";

type FieldErrors = Partial<Record<string, string[]>>;

const EMPTY_FORM: CreateFinanceCategoryRequest = {
    category_name: "",
    category_description: "",
    range_from: "",
    range_to: "",
    pastor_percentage: "",
    salary_floor: "",
    salary_ceiling: "",
    head_office_calc_type: "",
    head_office_rate: "",
    head_office_cap: "",
    head_office_fixed: "",
    district_fund_calc_type: "",
    district_fund_rate: "",
    district_fund_cap: "",
    district_fund_fixed: "",
};

const CreateFinanceCategoryModal: React.FC<{ show: boolean; onHide: () => void; onChanged?: () => void }> = ({ show, onHide, onChanged }) => {
    const resolvedNationId = useScopeId() ?? 0;
    const [form, setForm] = useState<CreateFinanceCategoryRequest>(EMPTY_FORM);
    const [formErrors, setFormErrors] = useState<FieldErrors>({});
    const showError = useApiError();
    const showValidation = useErrorModalStore((s) => s.showValidation);
    const { busy, doCreate } = useFinanceCategoryAction();
    const { calcTypes, calcTypesLoading, calcTypesError, fetchCalcTypes } = useFinanceCategoryOptions();

    useEffect(() => { fetchCalcTypes(); }, [fetchCalcTypes]);

    const handleChange = (field: keyof CreateFinanceCategoryRequest) => (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        const value = e.target.value;
        setForm((f) => ({ ...f, [field]: value }));
        if (formErrors[field as string]) {
            setFormErrors((prev) => ({ ...prev, [field]: undefined }));
        }
        // Clear conditional fields when calc type changes
        if (field === "head_office_calc_type") {
            setForm((f) => ({ ...f, head_office_calc_type: value, head_office_rate: "", head_office_cap: "", head_office_fixed: "" }));
        }
        if (field === "district_fund_calc_type") {
            setForm((f) => ({ ...f, district_fund_calc_type: value, district_fund_rate: "", district_fund_cap: "", district_fund_fixed: "" }));
        }
    };

    const resetForm = () => {
        setForm(EMPTY_FORM);
        setFormErrors({});
    };

    const handleSubmit = async () => {
        const errors: FieldErrors = {};
        const messages: string[] = [];

        if (!form.category_name?.trim()) {
            errors.category_name = ["Category name is required."];
            messages.push("Category name is required.");
        }
        if (!form.range_from) {
            errors.range_from = ["Range from is required."];
            messages.push("Range from is required.");
        }
        if (!form.pastor_percentage) {
            errors.pastor_percentage = ["Pastor percentage is required."];
            messages.push("Pastor percentage is required.");
        }
        if (!form.salary_floor) {
            errors.salary_floor = ["Salary floor is required."];
            messages.push("Salary floor is required.");
        }
        if (!form.salary_ceiling) {
            errors.salary_ceiling = ["Salary ceiling is required."];
            messages.push("Salary ceiling is required.");
        }
        if (!form.head_office_calc_type) {
            errors.head_office_calc_type = ["Head office calculation type is required."];
            messages.push("Head office calculation type is required.");
        }
        if (!form.district_fund_calc_type) {
            errors.district_fund_calc_type = ["District fund calculation type is required."];
            messages.push("District fund calculation type is required.");
        }

        if (messages.length > 0) {
            setFormErrors(errors);
            showValidation(messages);
            return;
        }

        try {
            const payload: CreateFinanceCategoryRequest = {
                ...form,
                range_to: form.range_to || undefined,
                category_description: form.category_description || undefined,
                head_office_rate: form.head_office_rate || undefined,
                head_office_cap: form.head_office_cap || undefined,
                head_office_fixed: form.head_office_fixed || undefined,
                district_fund_rate: form.district_fund_rate || undefined,
                district_fund_cap: form.district_fund_cap || undefined,
                district_fund_fixed: form.district_fund_fixed || undefined,
            };

            const response = await doCreate(resolvedNationId, payload);

            if (response.isSuccess()) {
                showSuccess(response.getData()?.message ?? '');
                onHide();
                resetForm();
                onChanged?.();
            } else {
                const error = response.getError() as ErrorResponse;
                const fieldErrors = (error?.errors as FieldErrors) ?? {};
                setFormErrors(fieldErrors);
                const list = (Object.values(fieldErrors).flat().filter(Boolean) as string[]);
                showValidation(list.length ? list : [error?.detail || "Failed to create finance category."]);
                showError(error);
            }
        } catch (err) {
            showError(err as ErrorResponse);
        }
    };

    const renderCalcTypeFields = (
        prefix: "head_office" | "district_fund",
        calcTypeField: "head_office_calc_type" | "district_fund_calc_type",
        rateField: "head_office_rate" | "district_fund_rate",
        capField: "head_office_cap" | "district_fund_cap",
        fixedField: "head_office_fixed" | "district_fund_fixed"
    ) => {
        const calcType = form[calcTypeField];
        const showRate = calcType === "PERCENTAGE" || calcType === "PERCENTAGE_WITH_CAP";
        const showCap = calcType === "PERCENTAGE_WITH_CAP";
        const showFixed = calcType === "FIXED_AMOUNT";

        return (
            <>
                <Form.Group className="mb-3" controlId={`${prefix}_calc_type`}>
                    <Form.Label>
                        Calculation Type <span className="text-danger">*</span>
                        <InfoTooltip text="How the distribution amount is calculated for this recipient." />
                    </Form.Label>
                    {calcTypesLoading ? (
                        <div className="d-flex align-items-center"><Spinner animation="border" size="sm" className="me-2" /><span>Loading…</span></div>
                    ) : calcTypesError ? (
                        <div className="text-danger small">Failed to load calculation types.</div>
                    ) : (
                        <Form.Select
                            value={form[calcTypeField] ?? ""}
                            onChange={handleChange(calcTypeField)}
                            isInvalid={!!formErrors[calcTypeField as string]}
                        >
                            <option value="">Select calculation type</option>
                            {calcTypes.map((ct) => (
                                <option key={ct.calc_type_code} value={ct.calc_type_code}>
                                    {ct.calc_type_name}
                                </option>
                            ))}
                        </Form.Select>
                    )}
                    <Form.Control.Feedback type="invalid">
                        {formErrors[calcTypeField as string]?.[0]}
                    </Form.Control.Feedback>
                </Form.Group>

                {showRate && (
                    <Form.Group className="mb-3" controlId={`${prefix}_rate`}>
                        <Form.Label>
                            Rate (%) <span className="text-danger">*</span>
                            <InfoTooltip text="Percentage of income allocated to this recipient (0–100)." />
                        </Form.Label>
                        <Form.Control
                            type="number"
                            placeholder="e.g. 10"
                            min="0"
                            max="100"
                            step="0.01"
                            value={form[rateField] ?? ""}
                            onChange={handleChange(rateField)}
                            isInvalid={!!formErrors[rateField as string]}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formErrors[rateField as string]?.[0]}
                        </Form.Control.Feedback>
                    </Form.Group>
                )}

                {showCap && (
                    <Form.Group className="mb-3" controlId={`${prefix}_cap`}>
                        <Form.Label>
                            Cap Amount <span className="text-danger">*</span>
                            <InfoTooltip text="Maximum dollar amount that can be distributed regardless of the calculated percentage." />
                        </Form.Label>
                        <Form.Control
                            type="number"
                            placeholder="e.g. 500.00"
                            min="0.01"
                            step="0.01"
                            value={form[capField] ?? ""}
                            onChange={handleChange(capField)}
                            isInvalid={!!formErrors[capField as string]}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formErrors[capField as string]?.[0]}
                        </Form.Control.Feedback>
                    </Form.Group>
                )}

                {showFixed && (
                    <Form.Group className="mb-3" controlId={`${prefix}_fixed`}>
                        <Form.Label>
                            Fixed Amount <span className="text-danger">*</span>
                            <InfoTooltip text="Fixed dollar amount distributed to this recipient." />
                        </Form.Label>
                        <Form.Control
                            type="number"
                            placeholder="e.g. 200.00"
                            min="0.01"
                            step="0.01"
                            value={form[fixedField] ?? ""}
                            onChange={handleChange(fixedField)}
                            isInvalid={!!formErrors[fixedField as string]}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formErrors[fixedField as string]?.[0]}
                        </Form.Control.Feedback>
                    </Form.Group>
                )}
            </>
        );
    };

    return (
        <Modal show={show} onHide={onHide} backdrop="static" keyboard={false} centered size="lg" data-testid="create-finance-category-modal">
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="fw-semibold">
                    <FiDollarSign className="me-2" aria-hidden />Create Finance Category
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-3" style={{ maxHeight: "75vh", overflowY: "auto" }}>
                <Form>
                    {/* Section 1: Basic Info */}
                    <Card className="mb-4 border-0 bg-light">
                        <Card.Body>
                            <h6 className="fw-semibold mb-3 text-muted text-uppercase small">Basic Info</h6>
                            <Form.Group className="mb-3" controlId="category_name">
                                <Form.Label>
                                    Category Name <span className="text-danger">*</span>
                                    <InfoTooltip text="A descriptive name for this income category, e.g. Standard Tithe." />
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter category name"
                                    value={form.category_name}
                                    onChange={handleChange("category_name")}
                                    isInvalid={!!formErrors.category_name}
                                />
                                <Form.Control.Feedback type="invalid">{formErrors.category_name?.[0]}</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="mb-0" controlId="category_description">
                                <Form.Label>Description<InfoTooltip text="Optional notes about this category." /></Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    placeholder="Enter description (optional)"
                                    value={form.category_description ?? ""}
                                    onChange={handleChange("category_description")}
                                    isInvalid={!!formErrors.category_description}
                                />
                                <Form.Control.Feedback type="invalid">{formErrors.category_description?.[0]}</Form.Control.Feedback>
                            </Form.Group>
                        </Card.Body>
                    </Card>

                    {/* Section 2: Range & Bands */}
                    <Card className="mb-4 border-0 bg-light">
                        <Card.Body>
                            <h6 className="fw-semibold mb-3 text-muted text-uppercase small">Range & Bands</h6>
                            <Row>
                                <Col xs={12} md={6}>
                                    <Form.Group className="mb-3" controlId="range_from">
                                        <Form.Label>
                                            Range From <span className="text-danger">*</span>
                                            <InfoTooltip text="Minimum income amount (inclusive) for this category to apply." />
                                        </Form.Label>
                                        <Form.Control
                                            type="number"
                                            placeholder="e.g. 0.01"
                                            min="0.01"
                                            step="0.01"
                                            value={form.range_from}
                                            onChange={handleChange("range_from")}
                                            isInvalid={!!formErrors.range_from}
                                        />
                                        <Form.Control.Feedback type="invalid">{formErrors.range_from?.[0]}</Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                                <Col xs={12} md={6}>
                                    <Form.Group className="mb-3" controlId="range_to">
                                        <Form.Label>
                                            Range To
                                            <InfoTooltip text="Maximum income amount (exclusive). Leave blank if this category applies to all amounts above Range From." />
                                        </Form.Label>
                                        <Form.Control
                                            type="number"
                                            placeholder="Leave blank for open-ended"
                                            min="0.01"
                                            step="0.01"
                                            value={form.range_to ?? ""}
                                            onChange={handleChange("range_to")}
                                            isInvalid={!!formErrors.range_to}
                                        />
                                        <Form.Control.Feedback type="invalid">{formErrors.range_to?.[0]}</Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Section 3: Pastor & Salary */}
                    <Card className="mb-4 border-0 bg-light">
                        <Card.Body>
                            <h6 className="fw-semibold mb-3 text-muted text-uppercase small">Pastor & Salary</h6>
                            <Row>
                                <Col xs={12} md={4}>
                                    <Form.Group className="mb-3" controlId="pastor_percentage">
                                        <Form.Label>
                                            Pastor % <span className="text-danger">*</span>
                                            <InfoTooltip text="Percentage of income allocated to the pastor (0–100)." />
                                        </Form.Label>
                                        <Form.Control
                                            type="number"
                                            placeholder="e.g. 47.5"
                                            min="0"
                                            max="100"
                                            step="0.01"
                                            value={form.pastor_percentage}
                                            onChange={handleChange("pastor_percentage")}
                                            isInvalid={!!formErrors.pastor_percentage}
                                        />
                                        <Form.Control.Feedback type="invalid">{formErrors.pastor_percentage?.[0]}</Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                                <Col xs={12} md={4}>
                                    <Form.Group className="mb-3" controlId="salary_floor">
                                        <Form.Label>
                                            Salary Floor <span className="text-danger">*</span>
                                            <InfoTooltip text="Minimum guaranteed salary amount." />
                                        </Form.Label>
                                        <Form.Control
                                            type="number"
                                            placeholder="e.g. 1000.00"
                                            min="0"
                                            step="0.01"
                                            value={form.salary_floor}
                                            onChange={handleChange("salary_floor")}
                                            isInvalid={!!formErrors.salary_floor}
                                        />
                                        <Form.Control.Feedback type="invalid">{formErrors.salary_floor?.[0]}</Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                                <Col xs={12} md={4}>
                                    <Form.Group className="mb-3" controlId="salary_ceiling">
                                        <Form.Label>
                                            Salary Ceiling <span className="text-danger">*</span>
                                            <InfoTooltip text="Maximum salary amount (must be ≥ salary floor)." />
                                        </Form.Label>
                                        <Form.Control
                                            type="number"
                                            placeholder="e.g. 5000.00"
                                            min="0"
                                            step="0.01"
                                            value={form.salary_ceiling}
                                            onChange={handleChange("salary_ceiling")}
                                            isInvalid={!!formErrors.salary_ceiling}
                                        />
                                        <Form.Control.Feedback type="invalid">{formErrors.salary_ceiling?.[0]}</Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Section 4: Head Office Distribution */}
                    <Card className="mb-4 border-0 bg-light">
                        <Card.Body>
                            <h6 className="fw-semibold mb-3 text-muted text-uppercase small">Head Office Distribution</h6>
                            {renderCalcTypeFields("head_office", "head_office_calc_type", "head_office_rate", "head_office_cap", "head_office_fixed")}
                        </Card.Body>
                    </Card>

                    {/* Section 5: District Fund Distribution */}
                    <Card className="mb-4 border-0 bg-light">
                        <Card.Body>
                            <h6 className="fw-semibold mb-3 text-muted text-uppercase small">District Fund Distribution</h6>
                            {renderCalcTypeFields("district_fund", "district_fund_calc_type", "district_fund_rate", "district_fund_cap", "district_fund_fixed")}
                        </Card.Body>
                    </Card>

                    <div className="d-flex justify-content-end mt-2">
                        <Button variant="primary" size="lg" className="btn-action" onClick={handleSubmit} disabled={busy} aria-busy={busy}>
                            {busy ? (
                                <><Spinner animation="border" size="sm" className="me-2" />Creating…</>
                            ) : (
                                <><FiPlus className="me-2" />Create Category</>
                            )}
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default CreateFinanceCategoryModal;
